import { isLocal } from './client';
import { getConfig } from './store';

export type CheckStatus = 'idle' | 'running' | 'pass' | 'fail' | 'warning';

export interface DiagnosticCheck {
  id: string;
  label: string;
  description: string;
  url: string;
  type: 'fetch' | 'socket' | 'image' | 'signed-image';
  severity: 'critical' | 'warning';
  allowlistDomain: string;
}

export interface CheckResult {
  id: string;
  status: CheckStatus;
  latencyMs?: number;
  error?: string;
}

export function getCuriLiveDiagnoseUrl(): string {
  if (isLocal()) return 'http://localhost:3001/diagnose';
  const config = getConfig();
  return `https://${config.game.url}/diagnose`;
}

export function getChecks(): DiagnosticCheck[] {
  const config = getConfig();
  const coreApiUrl = config.backend.externalApiUrl;
  const liveApiUrl = config.game.apiUrl;
  const cdnUrl = config.cdn.url;

  return [
    {
      id: 'api-core',
      label: 'Curipod API',
      description: 'Core platform services',
      url: `${coreApiUrl}/v0.5/connectivity/status`,
      type: 'fetch',
      severity: 'critical',
      allowlistDomain: new URL(coreApiUrl).hostname,
    },
    {
      id: 'game-api-http',
      label: 'Live Classroom (HTTP)',
      description: 'Real-time classroom server',
      url: `${liveApiUrl}/v0.5/connectivity/status`,
      type: 'fetch',
      severity: 'critical',
      allowlistDomain: new URL(liveApiUrl).hostname,
    },
    {
      id: 'game-api-ws',
      label: 'Live Classroom (WebSocket)',
      description: 'Real-time classroom connection',
      url: liveApiUrl,
      type: 'socket',
      severity: 'critical',
      allowlistDomain: new URL(liveApiUrl).hostname,
    },
    {
      id: 'cdn-images',
      label: 'Lesson Images',
      description: 'Image delivery for lessons',
      url: `${cdnUrl}/connectivity/connectivity-check.png`,
      type: 'image',
      severity: 'critical',
      allowlistDomain: new URL(cdnUrl).hostname,
    },
    {
      id: 'gcs-storage',
      label: 'Image Uploads',
      description: 'Student image upload service',
      url:
        'https://storage.googleapis.com/curipod-images-cdn-bucket/connectivity/connectivity-check.png',
      type: 'image',
      severity: 'critical',
      allowlistDomain: 'storage.googleapis.com',
    },
    {
      id: 'gcs-signed-image',
      label: 'Lesson Images (Signed URLs)',
      description: 'Authenticated image delivery with signed URLs',
      url: `${coreApiUrl}/v0.5/files/public/downloadUrl`,
      type: 'signed-image',
      severity: 'critical',
      allowlistDomain: 'storage.googleapis.com',
    },
  ];
}

async function runFetchCheck(url: string, timeoutMs = 8000): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      signal: controller.signal,
      mode: 'cors',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } finally {
    clearTimeout(timer);
  }
}

async function runSocketIOCheck(url: string, timeoutMs = 8000): Promise<void> {
  const { io } = await import('socket.io-client');

  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      socket.disconnect();
      reject(new Error('Timeout'));
    }, timeoutMs);

    const socket = io(`${url}/connectivity`, {
      transports: ['websocket'],
      timeout: timeoutMs,
    });

    socket.on('connect', () => {
      socket.emit('ping');
    });

    socket.on('pong', () => {
      clearTimeout(timer);
      socket.disconnect();
      resolve();
    });

    socket.on('connect_error', (err: Error) => {
      clearTimeout(timer);
      socket.disconnect();
      reject(err);
    });
  });
}

async function runImageCheck(url: string, timeoutMs = 8000): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Timeout')), timeoutMs);
    const img = new Image();
    img.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error('Image failed to load'));
    };
    img.src = `${url}?t=${Date.now()}`;
  });
}

async function runSignedImageCheck(
  signedUrlEndpoint: string,
  timeoutMs = 10000,
): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(signedUrlEndpoint, {
      method: 'POST',
      mode: 'cors',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'x-api-tenant': 'connectivity',
      },
      body: JSON.stringify({
        filename: 'connectivity-check.png',
        folder: 'sample',
      }),
    });
    if (!res.ok) throw new Error(`Signed URL request failed: HTTP ${res.status}`);
    const data = (await res.json()) as { url?: string };
    if (!data.url) throw new Error('Signed URL response missing url');

    const signedUrl = data.url;
    await new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Signed image failed to load'));
      img.src = signedUrl;
    });
  } finally {
    clearTimeout(timer);
  }
}

export async function runChecks(
  checks: DiagnosticCheck[],
  onUpdate: (id: string, result: CheckResult) => void,
): Promise<CheckResult[]> {
  const promises = checks.map(async check => {
    onUpdate(check.id, { id: check.id, status: 'running' });
    const start = performance.now();
    try {
      if (check.type === 'fetch') {
        await runFetchCheck(check.url);
      } else if (check.type === 'socket') {
        await runSocketIOCheck(check.url);
      } else if (check.type === 'signed-image') {
        await runSignedImageCheck(check.url);
      } else {
        await runImageCheck(check.url);
      }
      const latencyMs = Math.round(performance.now() - start);
      const result: CheckResult = { id: check.id, status: 'pass', latencyMs };
      onUpdate(check.id, result);
      return result;
    } catch (err) {
      const latencyMs = Math.round(performance.now() - start);
      const status: CheckStatus = check.severity === 'warning' ? 'warning' : 'fail';
      const result: CheckResult = {
        id: check.id,
        status,
        latencyMs,
        error: err instanceof Error ? err.message : 'Unknown error',
      };
      onUpdate(check.id, result);
      return result;
    }
  });
  return Promise.all(promises);
}

export function generateITAdminMessage(
  results: CheckResult[],
  checks: DiagnosticCheck[],
): string {
  const date = new Date().toISOString().split('T')[0];
  const lines = [
    `Curipod Connectivity Report — ${date}`,
    '==========================================',
  ];

  const failedDomains: string[] = [];

  for (const check of checks) {
    const result = results.find(r => r.id === check.id);
    if (!result) continue;
    const tag =
      result.status === 'pass' ? 'PASS' : result.status === 'warning' ? 'WARN' : 'FAIL';
    const latency = result.latencyMs ? ` (${result.latencyMs}ms)` : '';
    const error = result.error ? ` — ${result.error}` : '';
    lines.push(`[${tag}] ${check.label} — ${check.allowlistDomain}${latency}${error}`);

    if (result.status === 'fail' || result.status === 'warning') {
      failedDomains.push(check.allowlistDomain);
    }
  }

  if (failedDomains.length > 0) {
    lines.push('');
    lines.push(
      'Domains to allowlist in firewall AND web content filter (GoGuardian, Securly, LightSpeed):',
    );
    for (const domain of failedDomains) {
      lines.push(`  - ${domain}`);
    }
  }

  return lines.join('\n');
}

export interface SmartHint {
  text: string;
}

export function getSmartHints(
  results: CheckResult[],
  checks: DiagnosticCheck[],
): SmartHint[] {
  const hints: SmartHint[] = [];
  const getResult = (id: string) => results.find(r => r.id === id);

  const httpResult = getResult('game-api-http');
  const wsResult = getResult('game-api-ws');
  if (httpResult?.status === 'pass' && wsResult?.status === 'fail') {
    hints.push({
      text:
        'Your content filter may be blocking WebSocket connections. Ask your IT admin to allow WebSocket (wss://) traffic to api-live.curipod.com.',
    });
  }

  const allCriticalFailed = checks
    .filter(c => c.severity === 'critical')
    .every(c => getResult(c.id)?.status === 'fail');
  if (allCriticalFailed) {
    hints.push({
      text:
        'Your network may be blocking all Curipod services. If your district uses geo-location filtering, note that Curipod servers are located in the EU.',
    });
  }

  const cdnResult = getResult('cdn-images');
  const gcsResult = getResult('gcs-storage');
  const gcsSignedResult = getResult('gcs-signed-image');
  if (cdnResult?.status === 'fail' || gcsResult?.status === 'fail') {
    const domains = [
      cdnResult?.status === 'fail' && 'images-cdn.curipod.com',
      gcsResult?.status === 'fail' && 'storage.googleapis.com',
    ].filter(Boolean);
    hints.push({
      text: `Image delivery is blocked. Ask your IT admin to allow ${domains.join(
        ' and ',
      )}.`,
    });
  }

  if (gcsResult?.status === 'pass' && gcsSignedResult?.status === 'fail') {
    hints.push({
      text:
        'Your network allows storage.googleapis.com but blocks signed URLs (URLs with long query strings like X-Goog-Signature). Ask your IT admin to allow signed Google Cloud Storage URLs — content filters sometimes strip or block them.',
    });
  }

  return hints;
}
